import { type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Pause, Pencil, Play, RotateCcw, Trash2 } from 'lucide-react';

import { cn } from '@/shared/libs';
import { CountryFlag } from '@/shared/ui';
import { PROVIDER_STATUS_I18N_KEY, ProviderName } from '@entities/Provider';
import type { Provider } from '@psp-aggregator/sdk';

export interface ProviderCardProps {
  onDelete?: (provider: Provider) => void;
  onEdit: (provider: Provider) => void;
  onOpen: (provider: Provider) => void;
  onRestore?: (provider: Provider) => void;
  onToggleStatus: (provider: Provider) => void;
  provider: Provider;
}

export const ProviderCard: FC<ProviderCardProps> = ({
  onDelete,
  onEdit,
  onOpen,
  onRestore,
  onToggleStatus,
  provider,
}) => {
  const { t } = useTranslation();
  const status = provider.status ?? 'active';
  const isActive = status === 'active';
  const description =
    provider.short_description ||
    provider.full_description ||
    provider.description;

  return (
    <div
      className="flex cursor-pointer flex-col rounded-2xl border border-border/60 bg-card/60 p-5 backdrop-blur-xl transition-all hover:-translate-y-1 hover:border-brand/30 hover:shadow-lg hover:shadow-brand/5 dark:bg-white/[0.03]"
      role="button"
      tabIndex={0}
      onClick={() => onOpen(provider)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpen(provider);
        }
      }}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          {!!provider.logo_url && (
            <img
              alt=""
              className="h-11 w-11 shrink-0 rounded-xl border border-border object-cover"
              src={provider.logo_url}
            />
          )}
          <ProviderName alias={provider.alias} name={provider.real_name} />
        </div>
        <span
          className={cn(
            'shrink-0 rounded-full px-2 py-0.5 text-[10px]',
            isActive
              ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
              : 'bg-muted text-muted-foreground',
          )}>
          {t(PROVIDER_STATUS_I18N_KEY[status])}
        </span>
      </div>

      {description ? (
        <p className="mt-3 line-clamp-2 text-xs text-muted-foreground">
          {description}
        </p>
      ) : (
        <p className="mt-3 text-xs text-muted-foreground">
          {t('pages.admin.providers.cardDescriptionFallback')}
        </p>
      )}

      <div className="mt-3 flex flex-wrap gap-1">
        {(provider.industries ?? []).slice(0, 3).map((industry) => (
          <span
            key={industry}
            className="rounded-md bg-violet-500/10 px-1.5 py-0.5 text-[10px] font-medium text-violet-600 dark:text-violet-300">
            {industry}
          </span>
        ))}
        {(provider.countries ?? []).slice(0, 5).map((country) => (
          <span
            key={country}
            className="inline-flex items-center gap-1 rounded-md bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
            <CountryFlag className="text-[10px]" code={country} />
            {country}
          </span>
        ))}
      </div>

      <div className="mt-auto pt-4">
        <div
          className="flex items-center justify-between border-t border-border/40 pt-3"
          onClick={(event) => event.stopPropagation()}>
          <span className="text-xs text-muted-foreground">
            {t('pages.admin.providers.offersCount', {
              count: provider.offers_count ?? 0,
            })}
          </span>
          <div className="flex items-center gap-1">
            <button
              aria-label={t('buttons.edit')}
              className="cursor-pointer rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-brand/10 hover:text-brand"
              type="button"
              onClick={() => onEdit(provider)}>
              <Pencil className="h-3.5 w-3.5" />
            </button>
            {onRestore ? (
              <button
                aria-label={t('buttons.restore')}
                className="cursor-pointer rounded-lg p-1.5 text-emerald-600 transition-colors hover:bg-emerald-500/10 dark:text-emerald-400"
                type="button"
                onClick={() => onRestore(provider)}>
                <RotateCcw className="h-3.5 w-3.5" />
              </button>
            ) : (
              <button
                aria-label={isActive ? t('buttons.pause') : t('buttons.resume')}
                className={cn(
                  'cursor-pointer rounded-lg p-1.5 transition-colors',
                  isActive
                    ? 'text-amber-600 hover:bg-amber-500/10 dark:text-amber-400'
                    : 'text-emerald-600 hover:bg-emerald-500/10 dark:text-emerald-400',
                )}
                type="button"
                onClick={() => onToggleStatus(provider)}>
                {isActive ? (
                  <Pause className="h-3.5 w-3.5" />
                ) : (
                  <Play className="h-3.5 w-3.5" />
                )}
              </button>
            )}
            {!!onDelete && (
              <button
                aria-label={t('buttons.delete')}
                className="cursor-pointer rounded-lg p-1.5 text-rose-600 transition-colors hover:bg-rose-500/10 dark:text-rose-400"
                type="button"
                onClick={() => onDelete(provider)}>
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

ProviderCard.displayName = 'ProviderCard';
